import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {InboundConnector, SyncStatus} from '@/types/inbound-connector'

export interface UseInboundConnectorWithStatusResult {
    connector: InboundConnector | undefined
    syncStatus: SyncStatus | undefined
    isLoading: boolean
    error: Error | null
}

const QUERY_KEY = 'inbound-connectors'
const SYNC_STATUS_KEY = 'inbound-connectors-sync-status'

const SYNC_STATUS_POLL_MS = 3000

/**
 * Composite read hook for the InboundConnectorDetail page. Exposes the
 * connector record and a poll-driven sync-status snapshot so the page body
 * never has to derive query keys or polling intervals inline. The status
 * query polls only when the page is mounted (driven by TanStack's
 * {@code refetchInterval}); the connector query does not poll.
 *
 * Pass {@code id} as {@code undefined} during the brief gap before
 * {@code useParams} resolves to keep the hook safe under test renders.
 */
export function useInboundConnectorWithStatus(
    id: string | undefined,
): UseInboundConnectorWithStatusResult {
    const services = useServices()

    const connectorQuery = useQuery<InboundConnector>({
        queryKey: [QUERY_KEY, id],
        queryFn: () => services.inboundConnectors.detail(id!),
        enabled: !!id,
    })

    const syncStatusQuery = useQuery<SyncStatus>({
        queryKey: [SYNC_STATUS_KEY, id],
        queryFn: () => services.inboundConnectors.syncStatus(id!),
        enabled: !!id,
        refetchInterval: SYNC_STATUS_POLL_MS,
    })

    return {
        connector: connectorQuery.data,
        syncStatus: syncStatusQuery.data,
        isLoading: connectorQuery.isLoading,
        error: (connectorQuery.error as Error | null) ?? null,
    }
}
